import SearchableSelect from '../search/SearchableSelect';
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Collapse,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Button,
  Divider,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptUpload from './ReceiptUpload';
import AIGuidanceModal from './AIGuidanceModal';
import { aiEditCompliance } from '../../services/api';
import { isRealDisposition, resolveDisposition } from './dispositions';

const IRS_CATEGORIES = [
  'Cloud Infrastructure',
  'AI Services',
  'Software & SaaS',
  'Telecommunications',
  'Business Insurance',
  'Office Equipment',
  'Legal & Professional',
];

function TransactionReviewCard({ transaction, onSave, vendors = [] }) {
  const [expanded, setExpanded] = useState(
    transaction.review_status === 'pending'
  );

  // Field state — initialized from transaction data
  const [vendorName, setVendorName] = useState(transaction.vendor_name || '');
  const [category, setCategory] = useState(transaction.category || '');
  const [businessPct, setBusinessPct] = useState(
    transaction.business_pct != null ? transaction.business_pct : ''
  );
  const [rationale, setRationale] = useState(transaction.pct_rationale || '');
  const [briefPurpose, setBriefPurpose] = useState(transaction.business_purpose_brief || '');
  const [formalPurpose, setFormalPurpose] = useState(transaction.business_purpose_formal || '');
  // Registry writes are an explicit human action. AI receipt analysis must never
  // toggle this state, even when it describes a charge as monthly or recurring.
  const [addToVendorRegistry, setAddToVendorRegistry] = useState(false);
  const [disposition, setDisposition] = useState(resolveDisposition(transaction.disposition));
  const [voidReason, setVoidReason] = useState(
    transaction.disposition === 'void' ? (transaction.exclusion_reason || '') : ''
  );
  const [receiptSource, setReceiptSource] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [cachedReceiptAnalysis, setCachedReceiptAnalysis] = useState(transaction.receipt_analysis || '');

  // UI modes
  const [mode, setMode] = useState('display'); // 'display', 'edit', 'ai-edit'
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showSaveGuidance, setShowSaveGuidance] = useState(false);

  // AI Guidance Modal
  const [guidanceModalOpen, setGuidanceModalOpen] = useState(false);
  const [pendingReceiptFile, setPendingReceiptFile] = useState(null);

  // AI Edit
  const [aiEditRequest, setAiEditRequest] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  // Vendor reassign confirm
  const [reassignConfirmOpen, setReassignConfirmOpen] = useState(false);
  const [pendingVendorId, setPendingVendorId] = useState('');

  // Status helpers
  const isPending = !saved && transaction.review_status === 'pending';
  const isConfirmed = saved || transaction.review_status === 'auto_confirmed' || transaction.review_status === 'user_reviewed';
  const isVoided = disposition === 'void';
  const isMissingReceipt = !saved && !receiptFile && transaction.receipt_status === 'none'
    && disposition !== 'exclude' && disposition !== 'void';
  const hasFields = !!(vendorName.trim() && category && formalPurpose.trim());
  const missingFields = disposition !== 'claim' ? [] : [
    !vendorName.trim() && 'vendor name', !category && 'category',
    !formalPurpose.trim() && 'formal business purpose',
    (businessPct === '' || !Number.isFinite(Number(businessPct)) || Number(businessPct) < 0 || Number(businessPct) > 100) && 'business use (0–100%)',
  ].filter(Boolean);

  // Is this charge linked to an existing registry vendor (via dropdown selection or a
  // prior auto-match)? If so, saving preserves that link. Registry defaults change only
  // through the separately confirmed "Update vendor registry" action in the AI modal.
  const linkedVendorId = pendingVendorId || transaction.vendor_id;
  const linkedVendor = vendors.find(v => v.id === linkedVendorId) || null;

  const getStatusChip = () => {
    if (receiptFile && !saved) return <Chip label="Receipt ready to save" color="info" size="small" />;
    if (isPending) return <Chip icon={<NewReleasesIcon />} label="New Vendor" color="error" size="small" />;
    if (isMissingReceipt) return <Chip icon={<WarningIcon />} label="Missing Receipt" color="warning" size="small" />;
    if (isVoided) return <Chip icon={<CancelIcon />} label="Voided" color="warning" size="small" variant="outlined" />;
    if (isConfirmed && !saved && !isRealDisposition(transaction.disposition)) {
      return <Chip icon={<WarningIcon />} label="Needs decision" color="warning" size="small" />;
    }
    if (isConfirmed) return <Chip icon={<CheckCircleIcon />} label="Complete" color="success" size="small" />;
    return null;
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Analyze checkbox — opt-in: upload just attaches the receipt; check this to also run
  // AI vision analysis. Off by default so picking a known vendor + attaching a receipt is
  // the smooth path (no surprise AI modal on upload).
  const [analyzeOnUpload, setAnalyzeOnUpload] = useState(false);

  // === RECEIPT UPLOAD ===
  const handleReceiptSelected = (file) => {
    if (file) {
      setReceiptFile(file);
      setSaveError('');
      if (analyzeOnUpload) {
        setPendingReceiptFile(file);
        setGuidanceModalOpen(true);
      }
    }
  };

  const handleGenerateFromReceipt = () => {
    if (!receiptFile) return;
    setPendingReceiptFile(receiptFile);
    setGuidanceModalOpen(true);
  };

  // === AI Guidance Modal completes → populate fields ===
  const handleGuidanceComplete = (fields) => {
    if (fields.vendor_name) setVendorName(fields.vendor_name);
    if (fields.category) setCategory(fields.category);
    if (fields.business_pct != null) setBusinessPct(fields.business_pct);
    if (fields.pct_rationale) setRationale(fields.pct_rationale);
    if (fields.business_purpose_brief) setBriefPurpose(fields.business_purpose_brief);
    if (fields.business_purpose_formal) setFormalPurpose(fields.business_purpose_formal);
    if (fields.retrieval_steps) setReceiptSource(fields.retrieval_steps);
    if (fields.receipt_analysis) setCachedReceiptAnalysis(fields.receipt_analysis);
    if (fields._receiptFile) setReceiptFile(fields._receiptFile);
    setMode('display');
    setSaved(false);
    setWasReset(false);

    // Update vendor registry if user opted in
    if (fields._updateRegistry && transaction.vendor_id) {
      import('../../services/api').then(({ updateComplianceVendor }) => {
        updateComplianceVendor(transaction.vendor_id, {
          vendor_name: fields.vendor_name || vendorName,
          default_category: fields.category || category,
          default_business_pct: fields.business_pct != null ? fields.business_pct : businessPct,
          pct_rationale: fields.pct_rationale || rationale,
          default_business_purpose_brief: fields.business_purpose_brief || briefPurpose,
          default_business_purpose_formal: fields.business_purpose_formal || formalPurpose,
          retrieval_steps: fields.retrieval_steps || receiptSource || null,
        }).catch(err => console.error('Failed to update vendor registry:', err));
      });
    }
  };

  // Track if user reset the card (overrides DB receipt status)
  const [wasReset, setWasReset] = useState(false);

  // === Reset & Re-upload ===
  const handleReset = () => {
    setVendorName('');
    setCategory('');
    setBusinessPct('');
    setRationale('');
    setBriefPurpose('');
    setFormalPurpose('');
    setReceiptFile(null);
    setCachedReceiptAnalysis('');
    setAddToVendorRegistry(false);
    setMode('display');
    setSaved(false);
    setWasReset(true);
    setSaveError('');
    setShowSaveGuidance(false);
  };

  // === Vendor Reassign ===
  const applyVendorDefaults = (vendor) => {
    setVendorName(vendor.vendor_name);
    if (vendor.default_category) setCategory(vendor.default_category);
    if (vendor.default_business_pct != null) setBusinessPct(vendor.default_business_pct);
    if (vendor.pct_rationale) setRationale(vendor.pct_rationale);
    if (vendor.default_business_purpose_brief) setBriefPurpose(vendor.default_business_purpose_brief);
    if (vendor.default_business_purpose_formal) setFormalPurpose(vendor.default_business_purpose_formal);
    if (vendor.default_disposition) setDisposition(vendor.default_disposition);
  };

  const hasCompleteDefaults = (vendor) => (
    !!vendor &&
    !!vendor.default_business_purpose_brief &&
    !!vendor.default_business_purpose_formal
  );

  const handleVendorReassignRequest = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    if (!vendor) return;
    // Always capture the selected vendor id so Save links this transaction to the
    // existing registry vendor. Without this the backend only receives a name string
    // and manufactures a duplicate vendor entry.
    setPendingVendorId(vendorId);
    if (hasCompleteDefaults(vendor)) {
      applyVendorDefaults(vendor);
      return;
    }
    setReassignConfirmOpen(true);
  };

  const handleVendorReassignConfirm = (regenerate) => {
    const vendor = vendors.find(v => v.id === pendingVendorId);
    if (vendor) {
      applyVendorDefaults(vendor);
      if (regenerate && cachedReceiptAnalysis) {
        // Re-open guidance modal with cached receipt to regenerate
        setReassignConfirmOpen(false);
        setGuidanceModalOpen(true);
      } else {
        setReassignConfirmOpen(false);
      }
    } else {
      setReassignConfirmOpen(false);
    }
  };

  // === AI Edit ===
  const handleAiEdit = async () => {
    setAiLoading(true);
    setAiError('');
    try {
      const res = await aiEditCompliance({
        edit_request: aiEditRequest,
        current_fields: {
          vendor_name: vendorName,
          business_purpose_brief: briefPurpose,
          business_purpose_formal: formalPurpose,
          category: category,
          business_pct: businessPct,
          pct_rationale: rationale,
          amount: transaction.amount,
          date: transaction.date,
        },
        receipt_analysis: cachedReceiptAnalysis || '',
      });
      const data = res.data.data;
      if (data.vendor_name) setVendorName(data.vendor_name);
      if (data.category) setCategory(data.category);
      if (data.business_pct != null) setBusinessPct(data.business_pct);
      if (data.pct_rationale) setRationale(data.pct_rationale);
      if (data.business_purpose_brief) setBriefPurpose(data.business_purpose_brief);
      if (data.business_purpose_formal) setFormalPurpose(data.business_purpose_formal);
      setAiEditRequest('');
      setMode('display');
    } catch (err) {
      setAiError(err.response?.data?.msg || 'AI edit failed');
    } finally {
      setAiLoading(false);
    }
  };

  // === Save ===
  const handleSave = async () => {
    if (saving) return;
    if (missingFields.length) {
      setShowSaveGuidance(true);
      return;
    }
    setSaving(true);
    setSaveError('');
    const updatedTransaction = {
      ...transaction,
      vendor_name: vendorName,
      vendor_id: pendingVendorId || null,
      vendor_slug: transaction.vendor_slug || '',
      category,
      business_pct: parseInt(businessPct, 10) || 0,
      pct_rationale: rationale,
      business_purpose_brief: briefPurpose,
      business_purpose_formal: formalPurpose,
      registry_action: addToVendorRegistry ? 'add' : 'none',
      disposition,
      exclusion_reason: disposition === 'void' ? (voidReason || 'Cancelled / refunded') : undefined,
      review_status: 'user_reviewed',
      receipt_status: receiptFile ? 'uploaded' : transaction.receipt_status,
      // Manual dropdown correction relinks this transaction only. It deliberately does
      // NOT teach the vendor a new match pattern — that would re-pollute matching between
      // look-alike vendors. Pattern learning stays an explicit registry-edit action.
      learn_pattern: null,
      _receiptFile: receiptFile || null,
      retrieval_steps: receiptSource || null,
      receipt_analysis: cachedReceiptAnalysis || null,
    };
    try {
      await onSave(updatedTransaction);
      setSaved(true);
      setExpanded(false);
      setMode('display');
      setShowSaveGuidance(false);
    } catch (err) {
      setSaveError(err.response?.data?.msg || 'Could not save the charge and receipt. Your draft is still here; please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      sx={{
        mb: 1,
        border: isPending ? '2px solid' : '1px solid',
        borderColor: isPending ? 'error.main' : isMissingReceipt ? 'warning.main' : 'divider',
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>

        {/* === SUMMARY ROW (always visible) === */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', flexWrap: 'wrap', gap: 1 }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {vendorName || transaction.description}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ${transaction.amount.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(transaction.date)}
            </Typography>
            {businessPct !== '' && businessPct < 100 && (
              <Chip label={`${businessPct}%`} size="small" variant="outlined" />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusChip()}
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* === EXPANDED CONTENT === */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Card description: {transaction.description}
          </Typography>

          {/* === FIELD DISPLAY (read-only view of current fields) === */}
          {hasFields && mode === 'display' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip label={category} size="small" variant="outlined" color="primary" />
                <Chip label={`${businessPct}% business`} size="small" variant="outlined" />
                {businessPct < 100 && rationale && (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Rationale: {rationale}
                  </Typography>
                )}
              </Box>
              {formalPurpose && (
                <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'grey.50' }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{formalPurpose}</Typography>
                </Paper>
              )}
            </Box>
          )}

          {/* === RECEIPT === */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <ReceiptUpload
                receiptStatus={receiptFile ? 'selected' : (wasReset ? 'none' : transaction.receipt_status)}
                receiptFilename={receiptFile ? receiptFile.name : (wasReset ? null : transaction.receipt_filename)}
                hintText={transaction.receipt_hint}
                onUpload={handleReceiptSelected}
                transactionId={transaction.id}
              />
              {(wasReset || (!receiptFile && transaction.receipt_status === 'none')) && (
                <FormControlLabel
                  control={<Checkbox checked={analyzeOnUpload} onChange={(e) => setAnalyzeOnUpload(e.target.checked)} size="small" />}
                  label={<Typography variant="caption">Analyze with AI</Typography>}
                  sx={{ m: 0 }}
                />
              )}
            </Box>
          </Box>

          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {showSaveGuidance && missingFields.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Before saving, complete: {missingFields.join(', ')}. Generate with AI from your selected receipt, or enter the details manually.
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Button size="small" disabled={!receiptFile || saving} onClick={handleGenerateFromReceipt}>Generate with AI</Button>
                <Button size="small" disabled={saving} onClick={() => setMode('edit')}>Enter manually</Button>
              </Box>
              {!receiptFile && <Typography variant="caption">Select a receipt first to use AI.</Typography>}
            </Alert>
          )}

          {/* === ACTION BUTTONS (display mode) === */}
          {mode === 'display' && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
              {receiptFile && <Button size="small" startIcon={<AutoFixHighIcon />} disabled={saving} onClick={handleGenerateFromReceipt}>
                Generate with AI
              </Button>}
              {/* Always available so a pending "needs attention" charge can be assigned to an
                  existing registry vendor (or filled manually) — not only via receipt upload. */}
              <Button size="small" startIcon={<EditIcon />} onClick={() => setMode('edit')}>
                {hasFields ? 'Edit' : 'Assign vendor / edit'}
              </Button>
              {hasFields && (
                <>
                  <Button size="small" startIcon={<AutoFixHighIcon />} onClick={() => setMode('ai-edit')}>
                    AI Edit
                  </Button>
                  <Button size="small" startIcon={<RefreshIcon />} onClick={handleReset} color="warning">
                    Reset & Re-upload
                  </Button>
                </>
              )}
              {!hasFields && !receiptFile && transaction.receipt_status === 'none' && (
                <Typography variant="body2" color="text.secondary">
                  or upload a receipt to auto-fill.
                </Typography>
              )}
            </Box>
          )}

          {/* === AI EDIT MODE === */}
          {mode === 'ai-edit' && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>What do you want changed?</Typography>
              <TextField
                value={aiEditRequest}
                onChange={(e) => setAiEditRequest(e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="e.g., Change business use to 100%, this is dedicated office equipment"
                sx={{ mb: 2 }}
              />
              {aiError && <Alert severity="warning" sx={{ mb: 1 }}>{aiError}</Alert>}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={aiLoading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                  onClick={handleAiEdit}
                  disabled={aiLoading || !aiEditRequest.trim()}
                >
                  {aiLoading ? 'Editing...' : 'AI Edit Fields'}
                </Button>
                <Button onClick={() => { setMode('display'); setAiError(''); }}>Cancel</Button>
              </Box>
            </Paper>
          )}

          {/* === MANUAL EDIT MODE === */}
          {mode === 'edit' && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Edit charge</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* Assign to an existing registry vendor */}
                {vendors.length > 0 && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Assign to a known vendor (optional)</InputLabel>
                    <SearchableSelect
                      value=""
                      onChange={(e) => { if (e.target.value) handleVendorReassignRequest(e.target.value); }}
                      label="Assign to a known vendor (optional)"
                    >
                      <MenuItem value=""><em>Keep current</em></MenuItem>
                      {vendors.map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                          {v.vendor_name} — {v.default_category} ({v.default_business_pct != null ? v.default_business_pct + '%' : 'varies'})
                        </MenuItem>
                      ))}
                    </SearchableSelect>
                  </FormControl>
                )}

                <TextField label="Vendor Name" value={vendorName} onChange={(e) => setVendorName(e.target.value)} fullWidth size="small" />
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select value={category} onChange={(e) => setCategory(e.target.value)} label="Category" inputProps={{ 'aria-label': 'Category' }}>
                    {IRS_CATEGORIES.map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField label="Business Use %" type="number" value={businessPct} onChange={(e) => setBusinessPct(e.target.value)} size="small" sx={{ width: 150 }} inputProps={{ min: 0, max: 100 }} />
                  <TextField label="Rationale" value={rationale} onChange={(e) => setRationale(e.target.value)} size="small" fullWidth />
                </Box>
                <TextField label="Brief Purpose" value={briefPurpose} onChange={(e) => setBriefPurpose(e.target.value)} fullWidth size="small" multiline rows={2} />
                <TextField label="Formal Business Purpose" value={formalPurpose} onChange={(e) => setFormalPurpose(e.target.value)} fullWidth size="small" multiline rows={3}
                  helperText="Enter the business purpose yourself, or generate it with AI from the receipt." />
                <TextField label="Where do you get the receipt? (optional)" value={receiptSource} onChange={(e) => setReceiptSource(e.target.value)} fullWidth size="small" helperText="AI will format this into a standardized path" />

                {disposition === 'claim' && (
                  linkedVendor ? (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      Linked to your registry vendor <strong>"{linkedVendor.vendor_name}"</strong>.
                      Saving updates this charge only — it won't create or duplicate a registry entry.
                    </Alert>
                  ) : (
                    <FormControlLabel
                      control={<Checkbox checked={addToVendorRegistry} onChange={(e) => setAddToVendorRegistry(e.target.checked)} />}
                      label="Add as a new recurring vendor to your registry for future auto-match"
                    />
                  )
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" color="success" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Changes'}
                  </Button>
                  <Button onClick={() => setMode('display')}>Cancel</Button>
                </Box>
              </Box>
            </Paper>
          )}

          {/* === DISPOSITION + SAVE (display mode with fields) === */}
          {mode === 'display' && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                <RadioGroup row value={disposition} onChange={(e) => setDisposition(e.target.value)}>
                  <FormControlLabel value="claim" control={<Radio size="small" />} label="Claim" />
                  <FormControlLabel value="exclude" control={<Radio size="small" />} label="Exclude (personal)" />
                  <FormControlLabel value="void" control={<Radio size="small" />} label="Void (cancelled/refunded)" />
                </RadioGroup>
              </Box>
              {disposition === 'claim' && !linkedVendorId && (
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={<Checkbox size="small" checked={addToVendorRegistry} onChange={(e) => setAddToVendorRegistry(e.target.checked)} />}
                  label="Add to vendor registry as recurring (auto-match future charges)"
                />
              )}
              {disposition === 'void' && (
                <TextField
                  label="Void reason (optional)"
                  placeholder="Cancelled before delivery / Refund pending / etc."
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ mb: 1 }}
                />
              )}
              <Button variant="contained" color="success" size="small" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save & Confirm'}
              </Button>
            </Box>
          )}
        </Collapse>
      </CardContent>

      {/* === AI GUIDANCE MODAL === */}
      <AIGuidanceModal
        open={guidanceModalOpen}
        onClose={() => setGuidanceModalOpen(false)}
        transaction={transaction}
        receiptFile={pendingReceiptFile}
        onComplete={handleGuidanceComplete}
      />

      {/* === VENDOR REASSIGN CONFIRM === */}
      <Dialog open={reassignConfirmOpen} onClose={() => setReassignConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Vendor Changed</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Regenerate the AI description based on the new vendor?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            "Yes" will use the receipt analysis to generate new fields for this vendor.
            "No" will keep the current description and only change the vendor name.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleVendorReassignConfirm(false)}>No, keep description</Button>
          <Button variant="contained" onClick={() => handleVendorReassignConfirm(true)}>Yes, regenerate</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default TransactionReviewCard;
